import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dayjs } from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SearchIcon from "@mui/icons-material/Search";
import FileGrid from "./file-grid";
import FileIconGrid from "./file-icon-grid";
import DateRangeFilter from "./date-range-filter";
import StatusFilter from "./status-filter";
import SortControl from "./sort-control";
import ViewToggle, { FileExplorerViewMode } from "./view-toggle";
import SectionLoadingProgress from "../../shared/section-loading-progress";
import { FolderSummary, PartialBlob } from "../types";
import {
  BlobStatusFilter,
  filterBlobsByCreatedDateRange,
  filterBlobsByName,
  filterBlobsByStatus,
} from "../utils/filters";
import { BlobSortField, SortDirection, sortBlobs } from "../utils/sort";
import { formatDate, formatFileSize } from "../utils/formatters";
import { hasStatistics } from "../utils/folder-summary";
import {
  getStoredFileViewMode,
  setStoredFileViewMode,
} from "../utils/view-mode-storage";

interface FolderFilesViewProps {
  folder: FolderSummary;
  blobs: PartialBlob[];
  onBackToList: () => void;
  onDeleteFile: (blob: PartialBlob) => void;
  onDownloadFile: (blob: PartialBlob) => void;
  onRehydrateFile: (blob: PartialBlob) => void;
  onDeleteFiles: (blobs: PartialBlob[]) => void;
  onRehydrateFiles: (blobs: PartialBlob[]) => void;
  isLoadingMore?: boolean;
  expectedFileCount?: number | null;
  loadMoreError?: unknown;
  onRetryLoading?: () => void;
}

const FolderFilesView = ({
  folder,
  blobs,
  onBackToList,
  onDeleteFile,
  onDownloadFile,
  onRehydrateFile,
  onDeleteFiles,
  onRehydrateFiles,
  isLoadingMore = false,
  expectedFileCount = null,
  loadMoreError = undefined,
  onRetryLoading = () => {},
}: FolderFilesViewProps) => {
  const [selectedBlobs, setSelectedBlobs] = useState<PartialBlob[]>([]);
  const [fileSearch, setFileSearch] = useState("");
  const [createdFrom, setCreatedFrom] = useState<Dayjs | null>(null);
  const [createdTo, setCreatedTo] = useState<Dayjs | null>(null);
  const [statusFilter, setStatusFilter] = useState<BlobStatusFilter>("all");
  const [sortField, setSortField] = useState<BlobSortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [fileViewMode, setFileViewModeState] = useState<FileExplorerViewMode>(
    getStoredFileViewMode,
  );

  const setFileViewMode = (mode: FileExplorerViewMode) => {
    setFileViewModeState(mode);
    setStoredFileViewMode(mode);
  };

  // Filters always apply across every file in the folder before sorting or
  // pagination, so both stay consistent regardless of the active view mode.
  const filteredBlobs = useMemo(
    () =>
      sortBlobs(
        filterBlobsByStatus(
          filterBlobsByCreatedDateRange(
            filterBlobsByName(blobs, fileSearch),
            createdFrom,
            createdTo,
          ),
          statusFilter,
        ),
        sortField,
        sortDirection,
      ),
    [
      blobs,
      fileSearch,
      createdFrom,
      createdTo,
      statusFilter,
      sortField,
      sortDirection,
    ],
  );

  // If a filter/search/sort change hides a previously-selected file, drop it
  // from the selection so bulk actions can never target a file the user can
  // no longer see.
  useEffect(() => {
    setSelectedBlobs((prev) => {
      const visibleNames = new Set(filteredBlobs.map((blob) => blob.name));
      const next = prev.filter((blob) => visibleNames.has(blob.name));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredBlobs]);

  const handleBulkDelete = () => {
    onDeleteFiles(selectedBlobs);
    setSelectedBlobs([]);
  };

  const handleBulkRehydrate = () => {
    onRehydrateFiles(selectedBlobs);
    setSelectedBlobs([]);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "action.hover",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Tooltip title="Back to Folders" placement="top">
          <IconButton
            onClick={onBackToList}
            size="small"
            sx={{ color: "primary.main" }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Files - {folder.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasStatistics(folder) ? (
              <>
                {folder.fileCount} file{folder.fileCount === 1 ? "" : "s"} ·{" "}
                {formatFileSize(folder.totalSize)} · {folder.archivedCount}{" "}
                archived · Last modified {formatDate(folder.lastModifiedOn)}
              </>
            ) : (
              "Statistics not calculated yet"
            )}
          </Typography>
        </Box>
        {selectedBlobs.length > 0 && (
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              color="info"
              variant="outlined"
              startIcon={<CloudSyncIcon />}
              onClick={handleBulkRehydrate}
            >
              Rehydrate Selected ({selectedBlobs.length})
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedBlobs.length})
            </Button>
          </Box>
        )}
      </Box>

      <SectionLoadingProgress
        loadedCount={blobs.length}
        expectedCount={expectedFileCount}
        isLoadingMore={isLoadingMore}
        loadMoreError={loadMoreError}
        onRetry={onRetryLoading}
      />

      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search files"
            value={fileSearch}
            onChange={(e) => setFileSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                ),
              },
            }}
            sx={{ width: 220 }}
          />
          <DateRangeFilter
            from={createdFrom}
            to={createdTo}
            onFromChange={setCreatedFrom}
            onToChange={setCreatedTo}
          />
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          {fileViewMode === "icons" && (
            <SortControl
              field={sortField}
              direction={sortDirection}
              onFieldChange={setSortField}
              onDirectionChange={setSortDirection}
            />
          )}
        </Box>
        <ViewToggle value={fileViewMode} onChange={setFileViewMode} />
      </Box>

      {fileViewMode === "list" ? (
        <FileGrid
          blobs={filteredBlobs}
          selectedBlobs={selectedBlobs}
          onSelectionChange={setSelectedBlobs}
          onDelete={onDeleteFile}
          onDownload={onDownloadFile}
          onRehydrate={onRehydrateFile}
        />
      ) : (
        <FileIconGrid
          blobs={filteredBlobs}
          selectedBlobs={selectedBlobs}
          onSelectionChange={setSelectedBlobs}
          onDelete={onDeleteFile}
          onDownload={onDownloadFile}
          onRehydrate={onRehydrateFile}
        />
      )}
    </Box>
  );
};

export default FolderFilesView;
